import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, LoadingState, Chip } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Download, Trash, Eye, PaperPlaneTilt, Archive, UploadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const UPLOAD_CATEGORIES = [
    { id: "bylaws", label: "Bylaws" },
    { id: "agreement", label: "Agreement" },
    { id: "id_proof", label: "ID Proof" },
    { id: "certificate", label: "Certificate" },
    { id: "noc", label: "NOC" },
    { id: "invoice", label: "Invoice" },
    { id: "other", label: "Other" },
];

const VISIBILITY_OPTIONS = [
    { value: "COMMUNITY", label: "Community — all residents" },
    { value: "FLAT", label: "Specific flat(s)" },
    { value: "RESIDENT", label: "Specific resident(s)" },
    { value: "PRIVATE", label: "Private — single resident" },
    { value: "COMMITTEE", label: "Committee + Admin only" },
    { value: "ADMIN", label: "Admin only" },
];

const VISIBILITY_LABEL = { COMMUNITY: "Community", FLAT: "Flat", RESIDENT: "Resident", PRIVATE: "Private", COMMITTEE: "Committee+Admin", ADMIN: "Admin only" };

const CATEGORY_ICONS = {
    meeting_notice: "📢", mom: "📝", resolution: "✅", noc: "📄", auth_letter: "📜",
    maintenance_bill: "💰", demand_letter: "📨", custom: "📄",
    bylaws: "📚", agreement: "🤝", id_proof: "🪪", certificate: "🎓", invoice: "🧾", other: "📋",
};

export default function DocumentLibrary() {
    const { user } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterSource, setFilterSource] = useState("all");
    const [filterStatus, setFilterStatus] = useState("published");
    const [searchQuery, setSearchQuery] = useState("");
    const [flats, setFlats] = useState([]);
    const [residentOptions, setResidentOptions] = useState([]);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadCategory, setUploadCategory] = useState("other");
    const [uploadDescription, setUploadDescription] = useState("");
    const [uploadVisibility, setUploadVisibility] = useState("COMMUNITY");
    const [uploadFlatIds, setUploadFlatIds] = useState([]);
    const [uploadResidentIds, setUploadResidentIds] = useState([]);
    const [previewState, setPreviewState] = useState({ open: false, url: null, name: "", kind: "unsupported" });
    const isStaff = user?.role === "admin" || user?.role === "committee";
    const isAdmin = user?.role === "admin";

    useEffect(() => {
        loadDocuments();
    }, [filterCategory, filterSource, filterStatus]);

    useEffect(() => {
        // revoke the blob object URL on unmount to avoid leaking memory
        return () => {
            if (previewState.url) window.URL.revokeObjectURL(previewState.url);
        };
    }, [previewState.url]);

    useEffect(() => {
        if (!isStaff) return;
        (async () => {
            try {
                const [flatsRes, usersRes] = await Promise.all([api.get("/flats"), api.get("/users")]);
                setFlats(flatsRes.data);
                setResidentOptions(usersRes.data.filter(u => u.role === "resident"));
            } catch (e) {
                // upload targeting pickers are optional — library still works without them
            }
        })();
    }, [isStaff]);

    async function loadDocuments() {
        setLoading(true);
        try {
            const params = {};
            if (filterCategory !== "all") params.category = filterCategory;
            if (filterSource !== "all") params.source = filterSource;
            if (isStaff && filterStatus !== "all") params.status = filterStatus;
            const response = await api.get("/documents", { params });
            setDocuments(response.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    const displayName = (doc) => doc.template_name || doc.filename || "Document";

    async function downloadDocument(doc) {
        try {
            const response = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const blob = new Blob([response.data], { type: doc.content_type || "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = doc.filename || `${displayName(doc).replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success("Document downloaded!");
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function previewDocument(doc) {
        try {
            const response = await api.get(`/documents/${doc.id}/preview`, { responseType: "blob" });
            const contentType = doc.content_type || response.headers?.["content-type"] || "application/pdf";
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const kind = contentType.includes("pdf") ? "pdf" : contentType.includes("image") ? "image" : "unsupported";
            setPreviewState({ open: true, url, name: displayName(doc), kind });
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    function closePreview() {
        if (previewState.url) window.URL.revokeObjectURL(previewState.url);
        setPreviewState({ open: false, url: null, name: "", kind: "unsupported" });
    }

    async function emailDocument(doc) {
        try {
            const res = await api.post(`/documents/${doc.id}/email`);
            toast.success(`Queued for ${res.data.queued || 0} recipient(s)`);
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function archiveDocument(doc) {
        if (!window.confirm(`Archive "${displayName(doc)}"?`)) return;
        try {
            await api.post(`/documents/${doc.id}/archive`);
            toast.success("Document archived");
            loadDocuments();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function deleteDocument(doc) {
        if (!window.confirm(`Delete "${displayName(doc)}"?`)) return;
        try {
            await api.delete(`/documents/${doc.id}`);
            toast.success("Document deleted successfully");
            loadDocuments();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    function resetUploadForm() {
        setUploadFile(null);
        setUploadCategory("other");
        setUploadDescription("");
        setUploadVisibility("COMMUNITY");
        setUploadFlatIds([]);
        setUploadResidentIds([]);
    }

    function toggleUploadFlat(id) {
        setUploadFlatIds(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
    }

    function toggleUploadResident(id) {
        setUploadResidentIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    }

    async function submitUpload(e) {
        e.preventDefault();
        if (!uploadFile) {
            toast.error("Please choose a file");
            return;
        }
        if (uploadVisibility === "FLAT" && uploadFlatIds.length === 0) {
            toast.error("Select at least one flat");
            return;
        }
        if ((uploadVisibility === "RESIDENT" || uploadVisibility === "PRIVATE") && uploadResidentIds.length === 0) {
            toast.error("Select at least one resident");
            return;
        }
        const formData = new FormData();
        formData.append("file", uploadFile);
        formData.append("category", uploadCategory);
        if (uploadDescription) formData.append("description", uploadDescription);
        formData.append("visibility_type", uploadVisibility);
        if (uploadFlatIds.length) formData.append("flat_ids", uploadFlatIds.join(","));
        if (uploadResidentIds.length) formData.append("resident_ids", uploadResidentIds.join(","));

        setUploading(true);
        try {
            await api.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            setUploadOpen(false);
            resetUploadForm();
            loadDocuments();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setUploading(false);
        }
    }

    const filteredDocuments = documents.filter(d => {
        if (!searchQuery.trim()) return true;
        return displayName(d).toLowerCase().includes(searchQuery.toLowerCase());
    });

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Documents"
                title="Document Library"
                description="View, download and manage generated and uploaded documents"
                actions={isStaff && (
                    <Button onClick={() => setUploadOpen(true)} className="rounded-full" data-testid="upload-document-button">
                        <UploadSimple size={16} className="mr-2" />
                        Upload Document
                    </Button>
                )}
            />

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-3">
                        <MagnifyingGlass size={20} className="text-brand-inkSoft" />
                        <Input
                            data-testid="document-search-input"
                            placeholder="Search documents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1"
                        />
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger data-testid="document-category-filter">
                            <SelectValue placeholder="Filter by category" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            <SelectItem value="meeting_notice">📢 Meeting Notices</SelectItem>
                            <SelectItem value="mom">📝 Minutes of Meeting</SelectItem>
                            <SelectItem value="resolution">✅ Resolutions</SelectItem>
                            <SelectItem value="noc">📄 NOC</SelectItem>
                            {UPLOAD_CATEGORIES.filter(c => c.id !== "noc").map(c => (
                                <SelectItem key={c.id} value={c.id}>{CATEGORY_ICONS[c.id]} {c.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterSource} onValueChange={setFilterSource}>
                        <SelectTrigger data-testid="document-source-filter">
                            <SelectValue placeholder="Source" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Sources</SelectItem>
                            <SelectItem value="generated">Generated</SelectItem>
                            <SelectItem value="uploaded">Uploaded</SelectItem>
                        </SelectContent>
                    </Select>
                    {isStaff && (
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger data-testid="document-status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                </div>
            </div>

            {/* Documents List */}
            {filteredDocuments.length === 0 ? (
                <div className="bg-white border border-brand-line rounded-sm p-12 text-center" data-testid="document-library-empty">
                    <FileText size={48} className="mx-auto text-brand-inkSoft mb-4" />
                    <p className="text-brand-inkSoft">
                        {filterCategory !== "all" || filterSource !== "all" || searchQuery ? "No documents found" : "No documents yet"}
                    </p>
                </div>
            ) : (
                <div className="space-y-4" data-testid="document-library-list">
                    {filteredDocuments.map((doc) => {
                        const category = doc.category || doc.template_category || "other";
                        const visType = doc.visibility?.type || (doc.recipient_flats ? "FLAT" : "COMMUNITY");
                        return (
                            <div key={doc.id} className="bg-white border border-brand-line rounded-sm p-6 hover:shadow-md transition-shadow" data-testid={`document-card-${doc.id}`}>
                                <div className="flex items-start justify-between flex-wrap gap-4">
                                    <div className="flex items-start gap-4 flex-1 min-w-[220px]">
                                        <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-2xl flex-shrink-0">
                                            {CATEGORY_ICONS[category] || "📄"}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-heading text-lg font-semibold text-brand-ink mb-1">
                                                {displayName(doc)}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                                <Chip variant="default" className="text-xs">{category.replace(/_/g, " ").toUpperCase()}</Chip>
                                                <Chip variant={doc.source === "uploaded" ? "neutral" : "success"} className="text-xs">
                                                    {doc.source === "uploaded" ? "Uploaded" : "Generated"}
                                                </Chip>
                                                <Chip variant="default" className="text-xs">{VISIBILITY_LABEL[visType] || visType}</Chip>
                                                {doc.status === "archived" && <Chip variant="warn" className="text-xs">Archived</Chip>}
                                                <span className="text-xs text-brand-inkSoft">
                                                    Created by {doc.creator?.name || "Unknown"}
                                                </span>
                                                <span className="text-xs text-brand-inkSoft">
                                                    • {new Date(doc.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                                                </span>
                                            </div>
                                            {doc.signature?.applied && (
                                                <p className="text-xs text-brand-inkSoft mb-2">
                                                    Visually signed by {doc.signature.signed_by_name} ({doc.signature.signed_by_role}) — not a cryptographic e-signature
                                                </p>
                                            )}
                                            {doc.description && (
                                                <p className="text-sm text-brand-inkSoft mb-2">{doc.description}</p>
                                            )}
                                            {doc.field_values && Object.keys(doc.field_values).length > 0 && (
                                                <div className="bg-gray-50 rounded-sm p-3 text-sm">
                                                    <p className="text-xs text-brand-inkSoft mb-2 font-medium">Document Details:</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                        {Object.entries(doc.field_values).slice(0, 4).map(([key, value]) => (
                                                            <div key={key} className="text-xs">
                                                                <span className="text-brand-inkSoft">{key}:</span>{" "}
                                                                <span className="text-brand-ink font-medium">
                                                                    {String(value).length > 50 ? String(value).substring(0, 50) + "..." : value}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-2 flex-shrink-0 flex-wrap">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => previewDocument(doc)}
                                            className="rounded-full"
                                            data-testid={`document-preview-${doc.id}`}
                                        >
                                            <Eye size={14} className="mr-1" />
                                            Preview
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => downloadDocument(doc)}
                                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white"
                                            data-testid={`document-download-${doc.id}`}
                                        >
                                            <Download size={14} className="mr-1" />
                                            Download
                                        </Button>
                                        {isStaff && doc.status !== "archived" && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => emailDocument(doc)}
                                                className="rounded-full"
                                                data-testid={`document-email-${doc.id}`}
                                            >
                                                <PaperPlaneTilt size={14} className="mr-1" />
                                                Email
                                            </Button>
                                        )}
                                        {isStaff && doc.status !== "archived" && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => archiveDocument(doc)}
                                                className="rounded-full"
                                                data-testid={`document-archive-${doc.id}`}
                                            >
                                                <Archive size={14} />
                                            </Button>
                                        )}
                                        {isAdmin && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => deleteDocument(doc)}
                                                className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                                                data-testid={`document-delete-${doc.id}`}
                                            >
                                                <Trash size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upload Dialog */}
            <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) resetUploadForm(); }}>
                <DialogContent className="max-w-lg" data-testid="upload-document-dialog">
                    <DialogHeader>
                        <DialogTitle>Upload Document</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submitUpload} className="space-y-4">
                        <div className="space-y-2">
                            <Label>File * (PDF, JPG, PNG, WEBP, DOC, DOCX, XLS, XLSX — max 10MB)</Label>
                            <Input
                                type="file"
                                data-testid="upload-file-input"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={uploadCategory} onValueChange={setUploadCategory}>
                                <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {UPLOAD_CATEGORIES.map(c => (<SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Description (Optional)</Label>
                            <Textarea value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} rows={2} />
                        </div>
                        <div className="space-y-2">
                            <Label>Visibility *</Label>
                            <Select value={uploadVisibility} onValueChange={(v) => { setUploadVisibility(v); setUploadFlatIds([]); setUploadResidentIds([]); }}>
                                <SelectTrigger data-testid="upload-visibility-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VISIBILITY_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        {uploadVisibility === "FLAT" && (
                            <div className="border border-brand-line rounded-sm p-3 max-h-40 overflow-y-auto space-y-2">
                                {flats.map(flat => (
                                    <div key={flat.id} className="flex items-center space-x-2">
                                        <Checkbox checked={uploadFlatIds.includes(flat.id)} onCheckedChange={() => toggleUploadFlat(flat.id)} />
                                        <label className="text-sm">{flat.block}-{flat.number}</label>
                                    </div>
                                ))}
                            </div>
                        )}
                        {(uploadVisibility === "RESIDENT" || uploadVisibility === "PRIVATE") && (
                            <div className="border border-brand-line rounded-sm p-3 max-h-40 overflow-y-auto space-y-2">
                                {residentOptions.map(r => (
                                    <div key={r.id} className="flex items-center space-x-2">
                                        <Checkbox checked={uploadResidentIds.includes(r.id)} onCheckedChange={() => toggleUploadResident(r.id)} />
                                        <label className="text-sm">{r.name}</label>
                                    </div>
                                ))}
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={uploading} className="bg-brand-action hover:bg-brand-actionHover text-white" data-testid="submit-upload-button">
                                {uploading ? "Uploading..." : "Upload"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Preview Dialog — authenticated fetch -> blob -> object URL, rendered in-page (never
                window.open on a blob: URL, which browsers unreliably block/fail to render in a new tab) */}
            <Dialog open={previewState.open} onOpenChange={(v) => { if (!v) closePreview(); }}>
                <DialogContent className="max-w-4xl w-[90vw] h-[85vh] flex flex-col" data-testid="document-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>{previewState.name}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 bg-gray-50 rounded-sm overflow-hidden">
                        {previewState.kind === "pdf" && previewState.url && (
                            <iframe
                                src={previewState.url}
                                title="Document preview"
                                className="w-full h-full border-0"
                                data-testid="document-preview-frame"
                            />
                        )}
                        {previewState.kind === "image" && previewState.url && (
                            <div className="w-full h-full flex items-center justify-center p-4">
                                <img src={previewState.url} alt={previewState.name} className="max-w-full max-h-full object-contain" data-testid="document-preview-image" />
                            </div>
                        )}
                        {previewState.kind === "unsupported" && (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center p-6">
                                <FileText size={40} className="text-brand-inkSoft" />
                                <p className="text-brand-inkSoft text-sm">Preview isn't available for this file type. Please download it instead.</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closePreview} data-testid="close-preview-button">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
